// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { useLocales } from "react-localized"
import { IonCol, IonGrid, IonRow } from "@ionic/react"
import { Table, useWindowDimensions } from "../lib"
import { PurchaseOrder, PurchaseOrderProduct } from "../models"
import { OrderProductEditorEditContextPanel } from './OrderProductEditorEditContextPanel'
import '../lib/webid/variables.scss'


interface PurchaseOrderProductProps {
  children?: React.ReactElement | never[]
  editable?: boolean
  order: PurchaseOrder
}


const OrderProductEditor: React.FC<PurchaseOrderProductProps> = ({
  children,
  editable,
  order
}) => {
  const {gettext} = useLocales()
  const {height} = useWindowDimensions()
  const moneyFormatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: order.getCurrency()
  })
  return (
    <IonGrid className="pt-0" style={{display: "flex", flexDirection: "column", overflowY: "scroll"}}>
      <IonRow>
        <IonCol
          size="12"
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "0px",
            maxHeight: "var(--section-body-height)",
            overflowY: "hidden"
          }}
        >
          <Table
            contextPanel={OrderProductEditorEditContextPanel}
            contextPanelProps={{order: order}}
            columns={[
              {
                key: "_",
                label: gettext("#"),
                width: 15,
                render: (row: PurchaseOrderProduct, index: number) => { return (index + 1).toString() },
              },
              {
                key: "sku",
                label: gettext("SKU"),
                width: 120,
                render: (row: PurchaseOrderProduct) => { return row.getSku() || "—" },
                summarize: () => { return gettext("Total:")}
              },
              {
                key: "product",
                label: gettext("Product"),
                width: 180,
                render: (row: PurchaseOrderProduct) => { return row.getName() }
              },
              {
                key: "grade",
                label: gettext("Grade"),
                width: 60,
                render: (row: PurchaseOrderProduct) => { return row.getGrade() || "—"}
              },
              {
                key: "quantity",
                label: gettext("Quantity"),
                width: 90,
                render: (row: PurchaseOrderProduct) => { return row.getQuantity().toString() },
                summarize: (rows: PurchaseOrderProduct[]) => {
                  return rows.map((p) => p.getQuantity()).reduce((a, b) => a + b, 0).toString()
                }
              },
              {
                key: "price",
                label: gettext("Price"),
                width: 90,
                render: (row: PurchaseOrderProduct) => {
                  return moneyFormatter.format(row.getPrice())
                }
              },
              {
                key: "total",
                label: gettext("Total"),
                width: 90,
                render: (row: PurchaseOrderProduct) => { return moneyFormatter.format(row.getPrice() * row.getQuantity()) },
                summarize: (rows: PurchaseOrderProduct[]) => {
                  return moneyFormatter.format(
                    rows.map((p) => p.getQuantity() * p.getPrice()).reduce((a, b) => a + b, 0)
                  )
                }
              },
            ]}
            items={order.getProducts()}
            onDelete={(obj: PurchaseOrderProduct, index: number) => {
              order.removeLine(index)
            }}
            onRowClick={(obj: PurchaseOrderProduct, index: number) => {}}
          ></Table>
          {(!!children) ? (<>{children}</>) : null}
        </IonCol>
      </IonRow>
    </IonGrid>
  )
}


export {OrderProductEditor}